/**
 * Box plot renderer: quartiles, Tukey whiskers and outliers, side by side.
 *
 * Drawn the way the pie and the correlation matrix are - in a uPlot `draw`
 * hook over an invisible series - so resize, grid panels, dashboard tiles, the
 * tooltip plumbing and image export all come from the one shell. Unlike the
 * pie it keeps a real Y axis: a box is read against a scale.
 *
 * What it draws, per box: the box from Q1 to Q3, the median across it, the
 * mean as a small dot, whiskers to the furthest readings inside 1.5 x IQR, and
 * the outlier readings the server sent (up to 200, spread through the tail).
 * The reading count sits under each category's name on a single ship (an
 * overlay has one count per ship, so there it is the tooltip's); the tooltip
 * always has it, with the true outlier count.
 *
 * Colours follow the rest of Explore: one colour per signal in signals mode,
 * the signal's colour for every group in groups mode, and - with several ships
 * overlaid - the **Ship colours** mode from the gear, via the same colourer the
 * line and scatter overlays use.
 */

import uPlot from 'uplot';
import { plotState, type PlotState } from '../../../state/plot-state.ts';
import { buildSeriesColorer } from '../plot-constants.ts';
import { runtime } from '../plot-runtime.ts';
import { colorLegend, plotFocusShip } from '../plot-ui-state.ts';
import { themeColors, chartContainerSize, type ThemeColors } from './chart-dom.ts';
import { axisValueFont, buildYAxis, chartFontPx, rebuildUplot } from './uplot-helpers.ts';
import { escHtml, formatNum } from './format.ts';
import { compactNum } from './render-pie.ts';
import { shipLabel } from '../../../utils/ships.ts';
import type { BoxResponse, BoxStats } from '../../../api/types.ts';
import { placeTooltip } from './tooltip.ts';

/** A box's width as a share of its slot: room between neighbours, and between categories. */
const BOX_SLOT_FILL = 0.7;

/** Widest a box is drawn, in CSS px - a single box filling a wide chart reads as a wall. */
const BOX_MAX_PX = 90;

/** One box as drawn, with where it sits. `ship` is set only on a multi-ship overlay. */
export interface DrawnBox {
  stats: BoxStats;
  ship?: string;
  /** Category index (x position of the category's centre). */
  cat: number;
  /** Offset of this box's centre from the category's, in category units. */
  offset: number;
  /** Width in category units. */
  width: number;
  color: string;
}

/** A box response; on a multi-ship overlay each box carries its ship. */
export type BoxData = BoxResponse;

/**
 * Category order. Signals follow the picker (`box_columns`), so reordering
 * there redraws from what is loaded; groups keep the server's order (numeric
 * groups ascending, text alphabetical). Anything not listed keeps its place
 * after the rest.
 */
export function boxCategories(data: BoxData, state: PlotState): string[] {
  const seen: string[] = [];
  for (const b of data.boxes) if (!seen.includes(b.key)) seen.push(b.key);
  if (data.mode !== 'signals') return seen;
  const pos = new Map(state.box_columns.map((c, i) => [c, i]));
  return seen
    .map((k, i) => ({ k, i }))
    .sort((a, b) => (pos.get(a.k) ?? 1e6 + a.i) - (pos.get(b.k) ?? 1e6 + b.i))
    .map((x) => x.k);
}

/** Boxes with their category, slot and colour. */
export function layoutBoxes(
  data: BoxData,
  state: PlotState,
  focusShip: string | null = null,
): { categories: string[]; boxes: DrawnBox[] } {
  const categories = boxCategories(data, state);
  const ships: string[] = [];
  for (const b of data.boxes) if (b.ship && !ships.includes(b.ship)) ships.push(b.ship);
  const lanes = Math.max(1, ships.length);
  const slot = 1 / lanes;
  const width = slot * BOX_SLOT_FILL;
  // Colour by what the box *is*: a signal (signals mode) or the one signal
  // being split (groups mode) - so every group shares the signal's colour.
  const columnOf = (b: BoxStats): string => (data.mode === 'groups' ? (data.column ?? b.key) : b.key);
  const asSeries = (b: BoxStats): { name: string; column: string; ship?: string } => ({
    name: columnOf(b),
    column: columnOf(b),
    ...(b.ship ? { ship: b.ship } : {}),
  });
  const colorer = buildSeriesColorer(state, data.boxes.map(asSeries), focusShip);
  const columnIdx = new Map<string, number>();
  const boxes: DrawnBox[] = data.boxes.map((b) => {
    const col = columnOf(b);
    if (!columnIdx.has(col)) columnIdx.set(col, columnIdx.size);
    const lane = b.ship ? ships.indexOf(b.ship) : 0;
    return {
      stats: b,
      ...(b.ship ? { ship: b.ship } : {}),
      cat: categories.indexOf(b.key),
      offset: -0.5 + slot * (lane + 0.5),
      width,
      color: colorer(asSeries(b), columnIdx.get(col)!),
    };
  });
  return { categories, boxes };
}

/**
 * Y range covering every whisker and drawn outlier, padded so nothing sits on
 * the frame. A constant signal - a box of zero height - gets a band around its
 * value rather than a range of nothing.
 */
export function boxYRange(boxes: BoxStats[]): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const b of boxes) {
    lo = Math.min(lo, b.whisker_lo, ...b.outliers);
    hi = Math.max(hi, b.whisker_hi, ...b.outliers);
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [0, 1];
  if (hi === lo) {
    const pad = Math.abs(lo) * 0.1 || 1;
    return [lo - pad, hi + pad];
  }
  const pad = (hi - lo) * 0.06;
  return [lo - pad, hi + pad];
}

/** "1,234 readings · 12 outliers (all shown)" - the box's counts in words. */
export function outlierSummary(b: BoxStats): string {
  if (!b.n_outliers) return 'none';
  const drawn = b.outliers.length;
  return drawn >= b.n_outliers
    ? b.n_outliers.toLocaleString()
    : `${b.n_outliers.toLocaleString()} (${drawn} drawn)`;
}

/** Which drawn box an x position (in category units) falls in, or -1. */
export function boxIndexAt(boxes: DrawnBox[], x: number): number {
  let best = -1;
  let bestDist = Infinity;
  boxes.forEach((b, i) => {
    const centre = b.cat + b.offset;
    const dist = Math.abs(x - centre);
    // The whole lane counts, not only the painted box: a thin box would
    // otherwise be almost impossible to hover.
    const lane = b.width / BOX_SLOT_FILL / 2;
    if (dist <= lane && dist < bestDist) {
      best = i;
      bestDist = dist;
    }
  });
  return best;
}

interface BoxHover {
  index: number;
}

function truncateTo(ctx: CanvasRenderingContext2D, text: string, maxW: number): string {
  if (ctx.measureText(text).width <= maxW) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (ctx.measureText(`${text.slice(0, mid)}…`).width <= maxW) lo = mid;
    else hi = mid - 1;
  }
  return `${text.slice(0, Math.max(1, lo))}…`;
}

/** The draw hook that paints every box. Shared by the chart, grid panels and tiles. */
export function boxDrawHook(
  data: BoxData,
  c: ThemeColors,
  state: PlotState,
  hover: BoxHover,
  layout: { categories: string[]; boxes: DrawnBox[] },
): (u: uPlot) => void {
  return (u: uPlot): void => {
    const ctx = u.ctx;
    const d = uPlot.pxRatio || 1;
    const fontPx = Math.round(chartFontPx(state.style.font_size) * d);
    const lw = Math.max(1, Math.round(state.style.line_width * 0.75)) * d;
    const catPx = u.bbox.width / Math.max(1, layout.categories.length);
    const y = (v: number): number => u.valToPos(v, 'y', true);
    const x = (v: number): number => u.valToPos(v, 'x', true);

    ctx.save();
    ctx.beginPath();
    ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height);
    ctx.clip();

    layout.boxes.forEach((b, i) => {
      const s = b.stats;
      const cx = x(b.cat + b.offset);
      const half = Math.min((b.width * catPx) / 2, (BOX_MAX_PX * d) / 2);
      const cap = half * 0.5;
      const hovered = i === hover.index;
      const yq1 = y(s.q1);
      const yq3 = y(s.q3);

      // Whiskers: the stem from each quartile out to its whisker, and a cap.
      ctx.strokeStyle = b.color;
      ctx.lineWidth = lw;
      ctx.beginPath();
      ctx.moveTo(cx, yq3);
      ctx.lineTo(cx, y(s.whisker_hi));
      ctx.moveTo(cx - cap, y(s.whisker_hi));
      ctx.lineTo(cx + cap, y(s.whisker_hi));
      ctx.moveTo(cx, yq1);
      ctx.lineTo(cx, y(s.whisker_lo));
      ctx.moveTo(cx - cap, y(s.whisker_lo));
      ctx.lineTo(cx + cap, y(s.whisker_lo));
      ctx.stroke();

      // The box. A tinted fill so overlapping lanes stay readable, and a full
      // stroke so a box of zero height (a constant signal) is still a line.
      const top = Math.min(yq1, yq3);
      const h = Math.max(1, Math.abs(yq1 - yq3));
      ctx.globalAlpha = hovered ? 0.42 : 0.24;
      ctx.fillStyle = b.color;
      ctx.fillRect(cx - half, top, half * 2, h);
      ctx.globalAlpha = 1;
      ctx.lineWidth = hovered ? lw * 1.6 : lw;
      ctx.strokeRect(cx - half, top, half * 2, h);

      // Median: the line the eye is looking for, drawn heavier than the box.
      ctx.strokeStyle = c.textStrong;
      ctx.lineWidth = lw * 1.8;
      ctx.beginPath();
      ctx.moveTo(cx - half, y(s.median));
      ctx.lineTo(cx + half, y(s.median));
      ctx.stroke();

      // Mean: a small dot, so skew is visible at a glance without a legend.
      ctx.fillStyle = c.surface;
      ctx.strokeStyle = c.textStrong;
      ctx.lineWidth = d;
      ctx.beginPath();
      ctx.arc(cx, y(s.mean), Math.max(2, 2.5 * d), 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Outliers, hollow so a dense tail does not read as a solid bar.
      if (s.outliers.length) {
        const r = Math.max(1.5, (state.style.scatter_point_size * d) / 2);
        const jitter = Math.min(half * 0.6, 6 * d);
        ctx.strokeStyle = b.color;
        ctx.globalAlpha = state.style.scatter_alpha;
        ctx.lineWidth = d;
        s.outliers.forEach((v, k) => {
          // A fixed, repeatable spread - the same dots in the same places on
          // every redraw, rather than points that shuffle on hover.
          const dx = s.outliers.length > 1 ? ((((k * 7919) % 13) / 12) * 2 - 1) * jitter : 0;
          ctx.beginPath();
          ctx.arc(cx + dx, y(v), r, 0, Math.PI * 2);
          ctx.stroke();
        });
        ctx.globalAlpha = 1;
      }
    });

    ctx.restore();

    // Ship legend for an overlay, painted so an exported image still names
    // which colour is which ship. The chart's Legend setting hides it.
    const ships = [...new Set(layout.boxes.map((b) => b.ship).filter(Boolean))] as string[];
    if (ships.length > 1 && state.style.show_legend) {
      ctx.save();
      ctx.font = `500 ${fontPx}px system-ui, -apple-system, sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      const sw = Math.round(fontPx * 0.8);
      let lx = u.bbox.left + u.bbox.width;
      const ly = u.bbox.top + fontPx * 0.9;
      // Right-aligned, last ship rightmost - so the row grows leftwards.
      for (let i = ships.length - 1; i >= 0; i--) {
        const ship = ships[i]!;
        const name = shipLabel(ship);
        const w = ctx.measureText(name).width;
        lx -= w;
        const box = layout.boxes.find((b) => b.ship === ship)!;
        ctx.fillStyle = c.text;
        ctx.fillText(name, lx, ly);
        lx -= sw + 6 * d;
        ctx.fillStyle = box.color;
        ctx.fillRect(lx, ly - sw / 2, sw, sw);
        lx -= 14 * d;
      }
      ctx.restore();
    }
  };
}

function boxTooltipHook(
  data: BoxData,
  hover: BoxHover,
  layout: { categories: string[]; boxes: DrawnBox[] },
  target?: { el: HTMLElement; container: HTMLElement },
): (u: uPlot) => void {
  return (u: uPlot): void => {
    const tt = target?.el ?? runtime.tooltipEl;
    const left = u.cursor.left;
    const idx =
      u.over && left != null && left >= 0 ? boxIndexAt(layout.boxes, u.posToVal(left, 'x')) : -1;
    if (idx !== hover.index) {
      hover.index = idx;
      u.redraw(false, false);
    }
    if (!tt) return;
    const b = idx >= 0 ? layout.boxes[idx] : undefined;
    if (!b) {
      tt.classList.remove('visible');
      tt.dataset.ttIdx = '';
      return;
    }
    const key = `box:${idx}`;
    if (tt.dataset.ttIdx !== key) {
      const s = b.stats;
      const row = (name: string, val: string): string =>
        `<div class="uptt-row"><span class="uptt-name">${escHtml(name)}</span>` +
        `<span class="uptt-val">${escHtml(val)}</span></div>`;
      const what = data.mode === 'groups' ? `${data.group_column ?? ''} = ${s.key}` : s.key;
      const title = b.ship ? `${shipLabel(b.ship)} · ${what}` : what;
      const rows = [
        `<div class="uptt-x"><span class="uptt-swatch" style="background:${b.color}"></span> ${escHtml(title)}</div>`,
      ];
      if (data.mode === 'groups' && data.column) rows.push(row('signal', data.column));
      rows.push(row('median', formatNum(s.median)));
      rows.push(row('Q1 – Q3', `${formatNum(s.q1)} – ${formatNum(s.q3)}`));
      rows.push(row('whiskers', `${formatNum(s.whisker_lo)} – ${formatNum(s.whisker_hi)}`));
      rows.push(row('mean', formatNum(s.mean)));
      rows.push(row('min – max', `${formatNum(s.min)} – ${formatNum(s.max)}`));
      rows.push(row('outliers', outlierSummary(s)));
      rows.push(row('readings', s.n.toLocaleString()));
      if (s.n < 5) rows.push(`<div class="uptt-leg">Few readings - the quartiles are rough.</div>`);
      tt.innerHTML = rows.join('');
      tt.dataset.ttIdx = key;
    }
    tt.classList.add('visible');

    const container = target?.container ?? runtime.containerEl;
    if (container) placeTooltip(u, tt, container);
  };
}

/**
 * uPlot options for a box plot. `size`, the tooltip target and `state` let a
 * grid panel or a dashboard tile reuse the drawing; `yRange` lets grid panels
 * share one scale.
 */
export function buildBoxOpts(
  data: BoxData,
  c: ThemeColors,
  size: { width: number; height: number },
  target?: { el: HTMLElement; container: HTMLElement },
  state?: PlotState,
  yRange?: [number, number],
): { opts: uPlot.Options; uData: uPlot.AlignedData } {
  const s = state ?? plotState.value;
  const hover: BoxHover = { index: -1 };
  const layout = layoutBoxes(data, s, plotFocusShip.value);
  const n = Math.max(1, layout.categories.length);
  const [lo, hi] = yRange ?? boxYRange(data.boxes);
  const fontPx = chartFontPx(s.style.font_size);
  const uData: uPlot.AlignedData = [
    [-0.5, n - 0.5],
    [null, null],
  ];
  const yLabel = data.mode === 'groups' ? (data.column ?? '') : '';
  // One box per category (no overlay): its reading count goes under its name.
  const counts =
    layout.boxes.length === layout.categories.length
      ? layout.categories.map((k) => layout.boxes.find((b) => b.stats.key === k)?.stats.n ?? 0)
      : null;
  const opts: uPlot.Options = {
    ...size,
    scales: {
      x: { time: false, range: (): [number, number] => [-0.5, n - 0.5] },
      y: { range: (): [number, number] => [lo, hi] },
    },
    axes: [
      {
        scale: 'x',
        stroke: c.text,
        grid: { show: false },
        ticks: { stroke: c.grid, width: 1 },
        font: axisValueFont(fontPx),
        splits: () => layout.categories.map((_, i) => i),
        values: (u, splits) => {
          // Category names, cut to the width each one has.
          const room = (u.bbox.width / (uPlot.pxRatio || 1)) / n - 6;
          const ctx = u.ctx;
          ctx.save();
          ctx.font = axisValueFont(fontPx);
          const out = splits.map((i) => {
            const name = truncateTo(ctx, layout.categories[i] ?? '', room);
            return counts ? `${name}\nn ${compactNum(counts[i] ?? 0)}` : name;
          });
          ctx.restore();
          return out;
        },
        // Room for the second line when the counts are there.
        size: Math.round(fontPx * (counts ? 3.4 : 2.2)) + 8,
        ...(data.mode === 'groups' && data.group_column
          ? { label: data.group_column, labelSize: 22, labelFont: `600 ${Math.max(9, fontPx - 1)}px system-ui, -apple-system, sans-serif` }
          : {}),
      },
      buildYAxis(c, 'y', 'left', yLabel, fontPx),
    ],
    series: [{}, { label: 'box', paths: () => null, points: { show: false } }],
    legend: { show: false },
    cursor: {
      x: false,
      y: true,
      points: { show: false },
      drag: { x: false, y: false },
    },
    hooks: {
      draw: [boxDrawHook(data, c, s, hover, layout)],
      setCursor: [boxTooltipHook(data, hover, layout, target)],
    },
  };
  return { opts, uData };
}

/** No colour strip under a box plot: its colours name signals or ships, not a ramp. */
export function boxLegend(): void {
  colorLegend.set(null);
}

export function renderBox(data: BoxData): void {
  const c = themeColors();
  const { opts, uData } = buildBoxOpts(data, c, chartContainerSize());
  rebuildUplot(uData, opts);
  boxLegend();
}
